import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed, inject, Signal } from '@angular/core';
import { Router } from '@angular/router';
import {
  AddAffectedUsersRequest,
  AffectedUserListItem,
  Announcement,
  AnnouncementListItem,
  AnnouncementRequestType,
  AnnouncementsToggleOptions,
  CopyAnnouncementRequest,
  CreateAnnouncementRequest,
  CreateUserListRequest,
  UpdateAnnouncementRequest,
} from '@models/index';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, EMPTY, pipe, switchMap, tap } from 'rxjs';
import {
  addEntity,
  createCustomStateSetOnRegexHandler,
  createCustomStateSetOnUrlHandler,
  createStateResetHandler,
  deleteEntity,
  findAndRemove,
  findAndReplace,
  getAllWithParams,
  getOne,
  guidRegex,
  predicateForSorting,
  sortAlphabetically,
  StateDictionary,
  updateEntity,
} from '../helpers';
import { AnnouncementsService } from './announcements.service';

type AnnouncementsState = {
  announcements: AnnouncementListItem[];
  isLoading: boolean;
  isSelectedAnnouncementLoading: boolean;
  selectedAnnouncement: Announcement | null;
  isAnnouncementCreateLoading: boolean;

  affectedUsers: AffectedUserListItem[];
  isAffectedUsersLoading: boolean;
  affectedUsersCount: number;
};

const initialState: AnnouncementsState = {
  announcements: [],
  isLoading: false,
  isSelectedAnnouncementLoading: false,
  selectedAnnouncement: null,
  isAnnouncementCreateLoading: false,

  affectedUsers: [],
  isAffectedUsersLoading: false,
  affectedUsersCount: 0,
};

const announcementsDictionary: StateDictionary<Announcement> = {
  entityList: 'announcements',
  listLoading: 'isLoading',
  createLoading: 'isAnnouncementCreateLoading',
  selectedEntity: 'selectedAnnouncement',
  selectedEntityLoading: 'isSelectedAnnouncementLoading',
  entityId: 'id',
  sortFn: (a, b) => a.subject.localeCompare(b.subject),
};

export const AnnouncementsStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withDevtools('announcements-store'),

  withComputed(store => ({
    selectedAnnouncementId: computed(() => store.selectedAnnouncement()?.id || null),
  })),

  withMethods((store, announcementsService = inject(AnnouncementsService), router = inject(Router)) => {
    createStateResetHandler<AnnouncementsState>('/marketing/announcements')(store, initialState);
    createCustomStateSetOnUrlHandler<AnnouncementsState>('/marketing/announcements', { selectedAnnouncement: null })(store);
    createCustomStateSetOnRegexHandler<AnnouncementsState>(new RegExp(`^/marketing/announcements/(${guidRegex})/affected-users$`))(store, {
      affectedUsers: [],
      affectedUsersCount: 0,
    });

    const announcementMethods = {
      getAnnouncements: getAllWithParams<AnnouncementListItem, AnnouncementsToggleOptions>({
        store,
        service: announcementsService,
        dictionary: announcementsDictionary as unknown as StateDictionary<AnnouncementListItem>,
      }),

      getAnnouncement: getOne<Announcement>({
        store,
        service: announcementsService,
        dictionary: announcementsDictionary,
      }),

      addAnnouncement: addEntity<Announcement, CreateAnnouncementRequest | CopyAnnouncementRequest>({
        store,
        service: announcementsService,
        dictionary: announcementsDictionary,
        onSuccessCallback: newAnnouncement => {
          router.navigate(['marketing', 'announcements', newAnnouncement.id, 'details']);
        },
      }),

      updateAnnouncement: updateEntity<Announcement, UpdateAnnouncementRequest>({
        store,
        service: announcementsService,
        dictionary: announcementsDictionary,
        onSuccessCallback: updatedAnnouncement => {
          router.navigate(['marketing', 'announcements', updatedAnnouncement.id, 'details']);
        },
      }),

      deleteAnnouncement: deleteEntity<Announcement>({
        store,
        service: announcementsService,
        dictionary: announcementsDictionary,
        onSuccessCallback: () => {
          router.navigate(['marketing', 'announcements']);
        },
      }),

      createUserListForAnnouncement: rxMethod<{ id: string; request: CreateUserListRequest }>(
        pipe(
          tap(() => patchState(store, { isLoading: true })),
          switchMap(({ id, request }) =>
            announcementsService.createUserListForAnnouncement(id, request).pipe(
              tap(announcement => {
                patchState(store, {
                  announcements: sortAlphabetically(
                    findAndReplace(store.announcements(), convertToAnnouncementListItem(announcement), item => item.id === id),
                    predicateForSorting('subject'),
                  ),
                  selectedAnnouncement: announcement,
                  isLoading: false,
                });
                router.navigate(['marketing', 'announcements', id, 'details']);
              }),
              catchError(() => {
                patchState(store, { isLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),

      requestForAnnouncement: rxMethod<{ id: string; requestType: AnnouncementRequestType }>(
        pipe(
          tap(() => patchState(store, { isLoading: true, isSelectedAnnouncementLoading: true })),
          switchMap(({ id, requestType }) =>
            announcementsService.requestForAnnouncement(id, requestType).pipe(
              tap(announcement => {
                patchState(store, {
                  announcements: sortAlphabetically(
                    findAndReplace(store.announcements(), convertToAnnouncementListItem(announcement), item => item.id === id),
                    predicateForSorting('subject'),
                  ),
                  selectedAnnouncement: announcement,
                  isLoading: false,
                  isSelectedAnnouncementLoading: false,
                });
              }),
              catchError(() => {
                patchState(store, { isLoading: false, isSelectedAnnouncementLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),
    };

    const affectedUsersMethods = {
      getAffectedUsers: rxMethod<string>(
        pipe(
          tap(() => patchState(store, { isAffectedUsersLoading: true })),
          switchMap(id =>
            announcementsService.getAffectedUsers(id).pipe(
              tap(affectedUsers => {
                patchState(store, {
                  affectedUsers,
                  isAffectedUsersLoading: false,
                });
              }),
              catchError(() => {
                patchState(store, { isAffectedUsersLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),

      addAffectedUser: rxMethod<{ id: string; usersIds: AddAffectedUsersRequest[] }>(
        pipe(
          tap(() => patchState(store, { isAffectedUsersLoading: true })),
          switchMap(({ id, usersIds }) =>
            announcementsService.addAffectedUser(id, usersIds).pipe(
              tap(affectedUsers => {
                patchState(store, {
                  affectedUsers: [...store.affectedUsers(), ...affectedUsers],
                  isAffectedUsersLoading: false,
                });
              }),
              catchError(() => {
                patchState(store, { isAffectedUsersLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),

      deleteAffectedUser: rxMethod<{ id: string; userId: string }>(
        pipe(
          tap(() => patchState(store, { isAffectedUsersLoading: true })),
          switchMap(({ id, userId }) =>
            announcementsService.deleteAffectedUser(id, userId).pipe(
              tap(() => {
                patchState(store, {
                  affectedUsers: findAndRemove(store.affectedUsers(), item => item.id === userId),
                  isAffectedUsersLoading: false,
                });
              }),
              catchError(() => {
                patchState(store, { isAffectedUsersLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),

      getAffectedUsersCount: rxMethod<string>(
        pipe(
          tap(() => patchState(store, { isAffectedUsersLoading: true })),
          switchMap(id =>
            announcementsService.getAffectedUsersCount(id).pipe(
              tap(countData => {
                patchState(store, {
                  affectedUsersCount: countData.count,
                  isAffectedUsersLoading: false,
                });
              }),
              catchError(() => {
                patchState(store, { isAffectedUsersLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),
    };

    const navigationActions = {
      goToAnnouncementActions: async (id: string): Promise<boolean> => {
        const result = await router.navigate(['marketing', 'announcements', id]);
        if (!result) return false;

        if (!store.isSelectedAnnouncementLoading()) announcementMethods.getAnnouncement(id);
        return true;
      },
    };

    return {
      ...announcementMethods,
      ...affectedUsersMethods,
      ...navigationActions,
    };
  }),
);

const convertToAnnouncementListItem = (announcement: Announcement): AnnouncementListItem => ({
  ...announcement,
  validFromDate: announcement.validFromDate ? new Date(announcement.validFromDate) : null,
  validToDate: announcement.validToDate ? new Date(announcement.validToDate) : null,
});

export interface AnnouncementsStoreType {
  readonly announcements: Signal<AnnouncementListItem[]>;
  readonly isLoading: Signal<boolean>;
  readonly isSelectedAnnouncementLoading: Signal<boolean>;
  readonly selectedAnnouncement: Signal<Announcement | null>;
  readonly affectedUsers: Signal<AffectedUserListItem[]>;
  readonly isAffectedUsersLoading: Signal<boolean>;
  readonly affectedUsersCount: Signal<number>;
  readonly selectedAnnouncementId: Signal<string | null>;
  readonly isAnnouncementCreateLoading: Signal<boolean>;

  readonly getAnnouncements: (toggle: AnnouncementsToggleOptions) => void;
  readonly getAnnouncement: (id: string) => void;
  readonly addAnnouncement: (announcement: CreateAnnouncementRequest | CopyAnnouncementRequest) => void;
  readonly updateAnnouncement: (params: { id: string; dto: UpdateAnnouncementRequest }) => void;
  readonly deleteAnnouncement: (id: string) => void;
  readonly createUserListForAnnouncement: (params: { id: string; request: CreateUserListRequest }) => void;
  readonly requestForAnnouncement: (params: { id: string; requestType: AnnouncementRequestType }) => void;

  readonly getAffectedUsers: (id: string) => void;
  readonly addAffectedUser: (params: { id: string; usersIds: AddAffectedUsersRequest[] }) => void;
  readonly deleteAffectedUser: (params: { id: string; userId: string }) => void;
  readonly getAffectedUsersCount: (id: string) => void;

  readonly goToAnnouncementActions: (id: string) => Promise<boolean>;
}
